import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { sanityFetch, sanityImageUrl } from "../sanityClient";

type NewsletterIssue = {
  title: string;
  season: "Spring" | "Summer" | "Fall" | "Winter";
  year: number;
  publishedDate: string;
  description?: string;
  coverImage?: { asset: { _ref: string } };
  pdfUrl: string;
};

const seasonConfig = {
  Spring: { color: "#4a8c25", bg: "rgba(74,140,37,0.12)", border: "rgba(74,140,37,0.3)", gradient: "linear-gradient(135deg, #2d6a14 0%, #4a8c25 100%)", icon: "🌱" },
  Summer: { color: "#C19A3B", bg: "rgba(193,154,59,0.12)", border: "rgba(193,154,59,0.3)", gradient: "linear-gradient(135deg, #a07a20 0%, #C19A3B 100%)", icon: "☀️" },
  Fall:   { color: "#c6252d", bg: "rgba(198,37,45,0.1)",   border: "rgba(198,37,45,0.3)",   gradient: "linear-gradient(135deg, #8a1a20 0%, #c6252d 100%)", icon: "🍂" },
  Winter: { color: "#3a7abf", bg: "rgba(58,122,191,0.1)",  border: "rgba(58,122,191,0.3)",  gradient: "linear-gradient(135deg, #1f4a82 0%, #3a7abf 100%)", icon: "❄️" },
};

const contents = [
  { icon: "🧑‍🌾", label: "Vendor Articles" },
  { icon: "📅", label: "Upcoming Events" },
  { icon: "🤝", label: "Co-op Facts" },
  { icon: "⭐", label: "Member Highlights" },
  { icon: "👦", label: "Kids' Space" },
  { icon: "🍽️", label: "Seasonal Recipes" },
  { icon: "🗓️", label: "Market Dates" },
  { icon: "✍️", label: "Member Stories" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

function PlaceholderCover({ season, year }: { season: NewsletterIssue["season"]; year: number }) {
  const cfg = seasonConfig[season] ?? seasonConfig.Fall;
  return (
    <div
      style={{
        width: "100%",
        aspectRatio: "3/4",
        background: `linear-gradient(160deg, #0a1a08 0%, #0d1b2a 60%, #150308 100%)`,
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: cfg.gradient, opacity: 0.18 }} />
      <div
        style={{
          position: "absolute",
          top: -40, right: -40,
          width: 180, height: 180,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${cfg.color}33 0%, transparent 70%)`,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -30, left: -30,
          width: 140, height: 140,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)",
        }}
      />
      <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "1.5rem" }}>
        <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>{cfg.icon}</div>
        <div
          style={{
            fontSize: "0.7rem",
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: cfg.color,
            marginBottom: "0.5rem",
          }}
        >
          St. John's Farmers Market
        </div>
        <div
          style={{
            fontSize: "clamp(1.4rem, 4vw, 1.8rem)",
            fontWeight: 900,
            color: "white",
            lineHeight: 1.1,
          }}
        >
          {season}
          <br />
          <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 400, fontSize: "1rem" }}>{year}</span>
        </div>
        <div
          style={{
            marginTop: "1.25rem",
            width: 40,
            height: 3,
            background: cfg.gradient,
            borderRadius: 2,
            margin: "1.25rem auto 0",
          }}
        />
        <div
          style={{
            marginTop: "0.75rem",
            fontSize: "0.72rem",
            color: "rgba(255,255,255,0.35)",
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          Newsletter
        </div>
      </div>
    </div>
  );
}

function IssueCard({ issue, index }: { issue: NewsletterIssue; index: number }) {
  const cfg = seasonConfig[issue.season] ?? seasonConfig.Fall;

  return (
    <motion.div
      variants={fadeUp}
      style={{ height: "100%" }}
      custom={index}
    >
      <div
        style={{
          background: "#ffffff",
          border: "1px solid rgba(45,106,20,0.1)",
          borderRadius: 20,
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          transition: "transform 0.25s ease, box-shadow 0.25s ease",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.transform = "translateY(-6px)";
          (e.currentTarget as HTMLDivElement).style.boxShadow = "0 16px 40px rgba(0,0,0,0.13)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
          (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 24px rgba(0,0,0,0.07)";
        }}
      >
        {/* Cover */}
        <div style={{ position: "relative", padding: "1rem 1rem 0" }}>
          {issue.coverImage ? (
            <img
              src={sanityImageUrl(issue.coverImage as any)}
              alt={issue.title}
              style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", borderRadius: 12 }}
            />
          ) : (
            <PlaceholderCover season={issue.season} year={issue.year} />
          )}
          {/* Season badge */}
          <div
            style={{
              position: "absolute",
              top: "1.75rem",
              left: "1.75rem",
              background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              borderRadius: 999,
              padding: "0.25rem 0.75rem",
              backdropFilter: "blur(8px)",
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <span style={{ fontSize: "0.75rem" }}>{cfg.icon}</span>
            <span style={{ fontSize: "0.72rem", fontWeight: 800, color: cfg.color, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {issue.season}
            </span>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "1.25rem 1.25rem 1.5rem", flex: 1, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0d1b2a", margin: 0, lineHeight: 1.3 }}>
              {issue.title}
            </h3>
            <span style={{ fontSize: "1.1rem", fontWeight: 900, color: cfg.color, whiteSpace: "nowrap" }}>
              {issue.year}
            </span>
          </div>

          {issue.description && (
            <p style={{ color: "#5a6a50", fontSize: "0.88rem", lineHeight: 1.65, margin: 0, flex: 1 }}>
              {issue.description}
            </p>
          )}

          <div
            style={{
              borderTop: "1px solid rgba(45,106,20,0.1)",
              paddingTop: "1rem",
              marginTop: "auto",
            }}
          >
            <a
              href={issue.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block",
                background: cfg.gradient,
                color: "white",
                border: "none",
                borderRadius: 999,
                padding: "0.65rem 1rem",
                fontWeight: 700,
                fontSize: "0.88rem",
                textAlign: "center",
                textDecoration: "none",
                cursor: "pointer",
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.opacity = "0.88")}
              onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.opacity = "1")}
            >
              ↓ Download PDF
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Newsletter() {
  const [issues, setIssues] = useState<NewsletterIssue[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sanityFetch<NewsletterIssue[]>(
      `*[_type == "newsletter"] | order(publishedDate desc) {
        title, season, year, publishedDate, description, coverImage,
        "pdfUrl": pdfFile.asset->url
      }`
    ).then((data) => {
      setIssues(data);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      {/* ── HERO ── */}
      <section
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "clamp(80px, 12vw, 130px) 1.5rem clamp(90px, 14vw, 150px)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Glow blobs */}
        <div style={{ position: "absolute", top: -100, right: -80, width: 480, height: 480, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -120, left: -100, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.13) 0%, transparent 70%)", pointerEvents: "none" }} />

        {/* Subtle dot grid */}
        <div
          style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            backgroundImage: "radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp}>
              <span
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "rgba(109,199,68,0.1)", border: "1px solid rgba(109,199,68,0.28)",
                  borderRadius: 999, padding: "0.35rem 1.1rem",
                  color: "#6dc744", fontSize: "0.78rem", fontWeight: 800,
                  letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "1.5rem",
                }}
              >
                <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
                Co-operative Newsletter
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              style={{
                fontSize: "clamp(2.8rem, 7vw, 5rem)", fontWeight: 900,
                color: "white", lineHeight: 1.04, marginBottom: "1.5rem",
              }}
            >
              Our{" "}
              <span
                style={{
                  background: "linear-gradient(90deg, #6dc744 0%, #c6252d 100%)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}
              >
                Newsletter
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              style={{
                fontSize: "clamp(1rem, 2.2vw, 1.2rem)",
                color: "rgba(255,255,255,0.65)", lineHeight: 1.75,
                maxWidth: 620, margin: "0 auto 2.5rem",
              }}
            >
              Published quarterly by our member-run Newsletter Committee — packed with vendor
              stories, recipes, co-op updates, and everything happening at St. John's Farmers Market.
            </motion.p>

            {/* What's inside pills */}
            <motion.div
              variants={stagger}
              style={{
                display: "flex", flexWrap: "wrap",
                gap: "0.6rem", justifyContent: "center",
              }}
            >
              {contents.map((c) => (
                <motion.span
                  key={c.label}
                  variants={fadeUp}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 999, padding: "0.4rem 1rem",
                    color: "rgba(255,255,255,0.75)", fontSize: "0.82rem", fontWeight: 600,
                  }}
                >
                  <span>{c.icon}</span> {c.label}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Wave divider */}
        <div style={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: "100%", height: 60, display: "block" }}>
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#fffdf8" />
          </svg>
        </div>
      </section>

      {/* ── ARCHIVE ── */}
      <section style={{ background: "#fffdf8", padding: "clamp(60px, 8vw, 100px) 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} style={{ marginBottom: "3rem" }}>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
                <div>
                  <h2 style={{ fontSize: "clamp(1.7rem, 3.5vw, 2.4rem)", fontWeight: 900, color: "#0d1b2a", margin: 0 }}>
                    Past Issues
                  </h2>
                  <div style={{ width: 56, height: 4, background: "linear-gradient(90deg, #6dc744, #4a8c25)", borderRadius: 2, marginTop: 8 }} />
                </div>
                {!loading && issues.length > 0 && (
                  <span style={{ color: "#9aaa90", fontSize: "0.9rem", fontWeight: 600 }}>
                    {issues.length} issue{issues.length !== 1 ? "s" : ""} available
                  </span>
                )}
              </div>
            </motion.div>

            {loading && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1.75rem" }}>
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    style={{
                      borderRadius: 20, overflow: "hidden",
                      background: "linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.5s infinite",
                      height: 420,
                    }}
                  />
                ))}
              </div>
            )}

            {!loading && issues.length === 0 && (
              <motion.div variants={fadeUp}>
                <div
                  style={{
                    textAlign: "center", padding: "5rem 2rem",
                    background: "white", borderRadius: 24,
                    border: "2px dashed rgba(109,199,68,0.2)",
                  }}
                >
                  <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>📰</div>
                  <h3 style={{ color: "#0d1b2a", fontWeight: 800, marginBottom: "0.5rem" }}>Coming Soon</h3>
                  <p style={{ color: "#5a6a50", maxWidth: 400, margin: "0 auto" }}>
                    Our newsletter archive is being set up. Check back soon — past issues will appear here.
                  </p>
                </div>
              </motion.div>
            )}

            {!loading && issues.length > 0 && (
              <motion.div
                initial="hidden"
                animate="visible"
                variants={stagger}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: "1.75rem",
                }}
              >
                {issues.map((issue, i) => (
                  <IssueCard key={issue.publishedDate + issue.title} issue={issue} index={i} />
                ))}
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── ABOUT SECTION ── */}
      <section
        style={{
          background: "linear-gradient(160deg, #060d18 0%, #0d1b2e 50%, #081422 100%)",
          padding: "clamp(60px, 8vw, 100px) 1.5rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -80, right: -60, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -80, left: -60, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div
          style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        <div style={{ maxWidth: 960, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
            style={{ textAlign: "center" }}
          >
            <motion.div variants={fadeUp}>
              <span
                style={{
                  display: "inline-block",
                  background: "rgba(109,199,68,0.1)", border: "1px solid rgba(109,199,68,0.25)",
                  borderRadius: 999, padding: "0.3rem 1rem",
                  color: "#6dc744", fontSize: "0.78rem", fontWeight: 800,
                  letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "1.25rem",
                }}
              >
                About the Newsletter
              </span>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              style={{ fontSize: "clamp(1.7rem, 3.5vw, 2.5rem)", fontWeight: 900, color: "white", marginBottom: "1.25rem" }}
            >
              Written by Members, for Members
            </motion.h2>

            <motion.p
              variants={fadeUp}
              style={{
                fontSize: "1.05rem", color: "rgba(255,255,255,0.62)",
                lineHeight: 1.8, maxWidth: 680, margin: "0 auto 3rem",
              }}
            >
              Our newsletter is a labour of love by our volunteer Newsletter Committee. Published
              quarterly, it celebrates the stories and people that make St. John's Farmers Market
              a living co-operative community — and it's always free to read.
            </motion.p>

            <motion.div
              variants={stagger}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "1rem",
                textAlign: "left",
              }}
            >
              {[
                { label: "Published", value: "Quarterly", icon: "📆" },
                { label: "Format", value: "PDF Document", icon: "📄" },
                { label: "Cost", value: "Always Free", icon: "🎁" },
                { label: "By", value: "Member Volunteers", icon: "🤝" },
              ].map((stat) => (
                <motion.div
                  key={stat.label}
                  variants={fadeUp}
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 16,
                    padding: "1.25rem 1.5rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <span style={{ fontSize: "1.75rem" }}>{stat.icon}</span>
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{stat.label}</div>
                    <div style={{ fontSize: "1rem", fontWeight: 800, color: "white", marginTop: 2 }}>{stat.value}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}
